import moment from "moment-timezone";
import { DateTime } from "luxon";
import { timeZones } from "../constants/index";

const momentTimeZones = {
  [timeZones.at]: "Canada/Atlantic",
  [timeZones.et]: "America/New_York",
  [timeZones.ct]: "America/Chicago",
  [timeZones.mt]: "America/Mountain",
  [timeZones.pt]: "America/Los_Angeles",
  [timeZones.akt]: "US/Alaska",
  [timeZones.hat]: "US/Hawaii",
  [timeZones.st]: "US/Samoa",
  [timeZones.cht]: "Pacific/Guam"
};

const timeForTimeZones = {
  [timeZones.at]: "GMT-0400",
  ADT: "GMT-0400",
  [timeZones.akt]: "GMT-0900",
  AKDT: "GMT-0900",
  [timeZones.hat]: "GMT-1000",
  HST: "GMT-1000",
  [timeZones.st]: "GMT-1100",
  SST: "GMT-1100",
  [timeZones.cht]: "GMT+1000",
  ChST: "GMT+1000"
};

const durations = {
  days: "days",
  months: "months",
  years: "years"
};

export const formatDate = (date, format = "MM/DD/YYYY") =>
  moment(date).format(format);

export const formatDateInLocalTz = (date, format = "MM/DD/YYYY") =>
  moment(date)
    .local()
    .format(format);

export const daysFromToday = date => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = new Date(date) - today;
  return Math.floor(diff / (1000 * 3600 * 24));
};
export const daysBeforeToday = date => {
  return -1 * daysFromToday(date);
};

export const isFutureDate = date => new Date() - new Date(date) < 0;

/**
 * @param  {string} firstDate The date to check if it is before the second date
 * @param  {string} secondDate The date used to see if the first date comes before it
 * @returns true if the first date comes before the second date
 */
export const isBeforeDate = (firstDate, secondDate) =>
  dateToUTC(new Date(firstDate)) < dateToUTC(new Date(secondDate));

const dateToUTC = date => {
  return DateTime.utc(
    date.getUTCFullYear(),
    date.getUTCMonth() + 1,
    date.getUTCDate()
  );
};

/**
 * @typedef Duration
 * @property {number} value The number of days/months/years
 * @property {string} unit one of "days", "months", "years"
 */

/**
 * Returns an object with the number of years, months, or days in the number of days provided
 * @example
 * // returns {value: 2, unit: "months"}
 * getDaysDurationObject(60);
 *
 * @param  {number} numDays The number of days to use for the duration conversion
 * @returns {Duration}
 */

export const convertDaysToDurationObject = numDays => {
  if (numDays > 365 && numDays % 365 === 0) {
    return { value: Math.floor(numDays / 365), unit: durations.years };
  } else if (numDays >= 30 && numDays % 30 === 0) {
    return { value: Math.floor(numDays / 30), unit: durations.months };
  } else {
    return { value: numDays, unit: durations.days };
  }
};

/**
 * Returns the total number of days in the duration object
 * @example
 * // returns 60
 * convertDurationObjectToDays({value: 2, unit: "months"});
 *
 * @param {Duration} An object containing the duration unit and value
 * @returns  {number} numDays The number of total days from the duration object
 */

export const convertDurationObjectToDays = ({ value, unit }) => {
  if (unit === durations.years) {
    return value * 365;
  } else if (unit === durations.months) {
    return value * 30;
  } else {
    return value || 0;
  }
};

/**
 * Converts the number of days to the translated number of days/months/years
 * @example
 * // returns 2 months
 * translateDurationDays(t, 60);
 *
 *  * @example
 * // returns 1 month
 * translateDurationDays(t, 30);
 *
 * @param {Object} t The translation instance
 * @param {Duration} numDays The number of days to translate
 * @returns {string} The translated string based on the number of days
 */

export const translateDurationDays = (t, numDays) => {
  const { unit, value } = convertDaysToDurationObject(numDays);
  return t(`duration.${unit}.withCount`, { count: value });
};

export const getDurationString = (duration, t) => {
  if (!duration) return;
  const hours = Math.floor(duration / 60);
  const minutes = !!hours ? duration % 60 : duration;
  return hoursAndMinutesToString(hours, minutes, t);
};

export const replaceTimeZone = isoDate => {
  if (!isoDate) return;

  return DateTime.fromISO(isoDate, { setZone: true })
    .setZone(DateTime.local().zoneName, { keepLocalTime: true })
    .toJSDate();
};

/**
 * Converts the provided time into the correct timezone based on timeZone
 * and formats it with the provided dateFormat string
 *
 * @param  {string} time The time to convert in date time format
 * @param  {string} timeZone The timezone the time will be converted to
 * @param  {string} dateFormat
 * @returns A string with the converted and formatted date
 */
export const formatWithTimeZone = (time, timeZone, dateFormat) =>
  moment.tz(time, momentTimeZones[timeZone]).format(dateFormat);

/**
 * Gets the difference between two timestamps in milliseconds
 *
 * @param  {string} endDate The later date
 * @param  {string} startDate The earlier date
 * @returns The difference between the two times in milliseconds
 */
export const getTimeDifference = (endDate, startDate) =>
  moment(endDate).diff(moment(startDate));

/**
 * Checks if a time is within the amount of time provided from now
 *
 * @param  {string} time The time to check
 * @param  {number} timeFromNow The number in milliseconds to compare to the time
 * @returns {boolean} True if the time is +/- timeFromNow, false otherwise
 */
export const withinTimeFromNow = (time, timeFromNow) => {
  const timeDifference = Math.abs(getTimeDifference(Date.now(), time));
  return timeDifference <= timeFromNow;
};

export const isValidDate = date => !!date && moment(date).isValid();

export const toFullDate = dt => {
  if (!dt) return null;

  const _dt = new Date(dt);
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit"
  }).format(_dt);
};

export const formatIltDateTime = (date, time, timeZone) => {
  const subTime = time.substring(0, 8).trim();
  const gmtTime = timeForTimeZones[timeZone];
  if (gmtTime) return new Date(`${date} ${subTime} ${gmtTime}`);
  else return new Date(`${date} ${time}`);
};

export const convertToMinutes = (hours, minutes) => hours * 60 + minutes;

export const hoursAndMinutesToString = (hours, minutes, t) => {
  const hourString = !!hours ? t("hours", { count: hours }) : "";
  const minuteString = !!minutes ? t("minutes", { count: minutes }) : "";
  return hourString + " " + minuteString;
};

export const minutesOfDay = date =>
  moment(date).minutes() + moment(date).hours() * 60;

export const isDayEqual = (startDate, endDate) =>
  startDate && endDate && moment(startDate).isSame(endDate, "day");

export const isBeforeTime = (firstDate, secondDate) =>
  firstDate && secondDate && minutesOfDay(secondDate) > minutesOfDay(firstDate);

export const isAfterDay = (date, isAfterDate) =>
  moment(date).isAfter(isAfterDate, "day");
